import {
	Children,
	cloneElement,
	isValidElement,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactElement,
} from 'react';

import Styles from './index.module.scss';

import { useClickOutside } from '@/app/hooks/useClickOutside';
import { ChevronDownIcon } from '@/components/Elements/Icons';

export type SmartSelectOptionProps = {
	value: string | undefined;
	children: React.ReactNode;
} & React.HTMLAttributes<HTMLButtonElement>;

interface Props {
	value?: string | undefined;
	onChange?: (value: string | undefined) => void;
	children?: React.ReactNode;
	placeholder?: string;
}

export const SmartSelect = ({
	value,
	onChange,
	children,
	placeholder = 'Select an option',
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	const { ref } = useClickOutside<HTMLDivElement>(() => {
		setIsOpen(false);
		setSearchTerm('');
	});

	useEffect(() => {
		if (isOpen) {
			inputRef.current?.focus({ preventScroll: true });
		}
	}, [isOpen]);

	const options = useMemo(() => {
		return Children.toArray(children).filter(
			isValidElement
		) as ReactElement<SmartSelectOptionProps>[];
	}, [children]);

	const selectedOption = options.find((child) => child.props.value === value);

	const filteredOptions = useMemo(() => {
		if (!searchTerm) return options;
		return options.filter((option) =>
			(option.props.children as string).toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [options, searchTerm]);

	return (
		<div className={Styles.container} ref={ref}>
			{isOpen && (
				<input
					ref={inputRef}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>
			)}
			{!isOpen && (
				<button type="button" className={Styles.button} onClick={() => setIsOpen(!isOpen)}>
					{selectedOption ? selectedOption.props.children : placeholder}
				</button>
			)}
			<div className={Styles.icon}>
				<ChevronDownIcon />
			</div>
			{isOpen && (
				<div className={Styles.dropdown}>
					<div className={Styles.list}>
						{filteredOptions.map((child) =>
							cloneElement(child, {
								onClick: () => {
									onChange?.(child.props.value);
									setIsOpen(false);
									setSearchTerm('');
								},
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
};

SmartSelect.Option = ({ children, ...props }: SmartSelectOptionProps) => {
	return (
		<button type="button" {...props}>
			{children}
		</button>
	);
};
